import * as XLSX from 'xlsx';
import i18n from '../../i18n';
import { IBatch } from '../../@types/API/IBatches';
import { getBatchClassification } from '../batches/getBatchClassification';
import { applyColumnWidthsFromArrays } from './columnWidthCalculator';

interface IBatchCategory {
  content: IBatchCategoryContent[];
  pageable: any;
  total_elements: number;
  total_pages: number;
  last: boolean;
  size: number;
  number: number;
  sort: any;
  number_of_elements: number;
  first: boolean;
  empty: boolean;
}

interface IBatchCategoryContent {
  batch_id: number;
  initials: string;
  batch_capacity_rate: number;
  status: string;
  farm_name: string;
  farm_initials: string;
  area_name: string;
  area_initials: string;
  area_acreage: number;
  micro_area: string;
  micro_area_initials: string;
  categories: Category[];
}

interface Category {
  id: number;
  name: string;
  month_reference: number;
  quantity: number;
  age_description: string;
  unit: string;
}

export const exportBatchesDataToExcel = (
  batchCategory: IBatchCategory | undefined,
  batches: Pick<IBatch, "id" | "batch_label" | "batch_stamp">[] = [],
) => {
  if (!batchCategory || !batchCategory.content || batchCategory.content.length === 0) {
    throw new Error(i18n.t("reports.batchExport.insufficientData"));
  }

  const batchesById = new Map(batches.map(batch => [Number(batch.id), batch]));

  const maleCategoryIds = [6, 7, 8, 9, 10, 11];
  const femaleCategoryIds = [1, 2, 3, 4, 5];

  const rows: any[][] = [];

  const headerRow: any[] = [
    i18n.t("reports.batches"),
    i18n.t("reports.batchExport.classification"),
    i18n.t("reports.paddock"),
    i18n.t("reports.male"),
    ...maleCategoryIds.slice(1).map(() => ''),
    i18n.t("reports.female"),
    ...femaleCategoryIds.slice(1).map(() => ''),
    i18n.t("reports.balanceLabel")
  ];
  rows.push(headerRow);

  const subHeaderRow: any[] = [
    '',
    '',
    '',
    i18n.t("reports.batchExport.ageRange", { start: 0, end: 8 }),
    i18n.t("reports.batchExport.ageRange", { start: 9, end: 12 }),
    i18n.t("reports.batchExport.ageRange", { start: 13, end: 24 }),
    i18n.t("reports.batchExport.ageRange", { start: 25, end: 36 }),
    i18n.t("reports.batchExport.overAge", { age: 37 }),
    i18n.t("reports.bulls"),
    i18n.t("reports.batchExport.ageRange", { start: 0, end: 8 }),
    i18n.t("reports.batchExport.ageRange", { start: 9, end: 12 }),
    i18n.t("reports.batchExport.ageRange", { start: 13, end: 24 }),
    i18n.t("reports.batchExport.ageRange", { start: 25, end: 36 }),
    i18n.t("reports.batchExport.overAge", { age: 37 }),
    ''
  ];
  rows.push(subHeaderRow);

  batchCategory.content.forEach((batch) => {
    const row: any[] = [
      `${batch.farm_initials}-${batch.area_initials}-${batch.initials}`,
      getBatchClassification(batchesById.get(Number(batch.batch_id)), i18n.language),
      `${batch.farm_initials}-${batch.area_initials}-${batch.micro_area_initials}`,
      ...maleCategoryIds.map((id) => {
        const category = batch.categories.find((cat) => cat.id === id);
        return category?.quantity || 0;
      }),
      ...femaleCategoryIds.map((id) => {
        const category = batch.categories.find((cat) => cat.id === id);
        return category?.quantity || 0;
      }),
      batch.categories.reduce((sum, category) => sum + category.quantity, 0)
    ];
    rows.push(row);
  });

  const totalRow: any[] = [i18n.t("reports.total"), "", ""];
  
  maleCategoryIds.forEach((id) => {
    const total = batchCategory.content.reduce((sum, batch) => {
      const category = batch.categories.find((cat) => cat.id === id);
      return sum + (category?.quantity || 0);
    }, 0);
    totalRow.push(total);
  });
  
  femaleCategoryIds.forEach((id) => {
    const total = batchCategory.content.reduce((sum, batch) => {
      const category = batch.categories.find((cat) => cat.id === id);
      return sum + (category?.quantity || 0);
    }, 0);
    totalRow.push(total);
  });
  
  const grandTotal = batchCategory.content.reduce((sum, batch) => {
    return sum + batch.categories.reduce((batchSum, category) => batchSum + category.quantity, 0);
  }, 0);
  totalRow.push(grandTotal);
  
  rows.push(totalRow);

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet['!merges'] = [
    XLSX.utils.decode_range('A1:A2'),
    XLSX.utils.decode_range('B1:B2'),
    XLSX.utils.decode_range('C1:C2'),
    XLSX.utils.decode_range('D1:I1'),
    XLSX.utils.decode_range('J1:N1'),
    XLSX.utils.decode_range('O1:O2'),
  ];
  applyColumnWidthsFromArrays(worksheet, rows);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, i18n.t("reports.batchExport.sheet"));

  const excelBuffer = XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array' 
  });

  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${i18n.t("reports.batchExport.filename")}-${new Date().toISOString().split('T')[0]}.xlsx`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
